import { useEffect } from 'react';

import { useGateValue, useStatsigClient } from '@coursera-web/statsig';

import { IdTypes, createFeatureScope } from 'bundles/product-features';
import { ORG_SCOPED_HOME_STATSIG_GATE } from 'bundles/unified-common/constants/orgScopedHomeStatsig';

const { useProductFeatures } = /* @__PURE__ */ createFeatureScope({
  enterprise: {
    enableOrgScopedHome: false,
  },
});

/**
 * Checks if a specific organization is opted into org-scoped home.
 *
 * Unlike useIsOrgScopedHome (which reads from page-level context),
 * this hook fetches the enableOrgScopedHome product feature for a given org ID.
 * Used in the program switcher to determine per-org link behavior.
 *
 * Requires BOTH:
 * 1. The Statsig gate `org-scoped-home` is passing (user-level rollout)
 * 2. The `enableOrgScopedHome` product feature is enabled for the given org
 */
export function useIsOrgOptedIntoOrgScopedHome(orgId: string): boolean {
  const userInOrgScopedRollout = useGateValue(ORG_SCOPED_HOME_STATSIG_GATE, { disableExposureLog: true });
  const { checkGate } = useStatsigClient();

  const { features } = useProductFeatures({
    idType: IdTypes.Organization,
    id: orgId,
    skip: !orgId,
  });

  const featureEnabled = orgId ? features.get('enterprise', 'enableOrgScopedHome') : false;
  const isEnabled = userInOrgScopedRollout && featureEnabled;

  useEffect(() => {
    if (isEnabled) {
      checkGate(ORG_SCOPED_HOME_STATSIG_GATE);
    }
  }, [isEnabled, checkGate]);

  return isEnabled;
}
